import PubNub from 'pubnub';
import {
  BloodOxyType,
  VitalMessage,
  VitalSensors,
  VitalsDataPayload,
} from './model';

type MappedVitalSensor = Exclude<VitalSensors, VitalSensors.ECG>;

const VitalSensorsCamelCase: Record<
  MappedVitalSensor,
  keyof VitalsDataPayload
> = {
  [VitalSensors.BLOOD_PRESSURE]: 'bloodPressure',
  [VitalSensors.BLOOD_OXY]: 'bloodOxy',
  [VitalSensors.TEMPERATURE]: 'temperature',
  [VitalSensors.GLUCOSE]: 'glucose',
  [VitalSensors.MANUAL_WEIGHT]: 'manualWeight',
  [VitalSensors.MANUAL_HEIGHT]: 'manualHeight',
  [VitalSensors.MANUAL_NOTES]: 'manualNotes',
};

const isKnownSensor = (typeString: string): typeString is MappedVitalSensor =>
  typeString in VitalSensorsCamelCase;

const shouldSkipBloodOxyUpdate = (
  current: BloodOxyType | undefined,
  next: BloodOxyType
) =>
  next.SpO2 === 0 &&
  current !== undefined &&
  current.SpO2 > 0;

export const generateVitalObject = (
  message: VitalMessage
): Partial<VitalsDataPayload> => {
  if (!isKnownSensor(message.typeString)) {
    // eslint-disable-next-line no-console
    console.error('PubNub: Unknown sensor type', message.typeString);
    return {};
  }

  const stateKey = VitalSensorsCamelCase[message.typeString];
  return { [stateKey]: message.data };
};

export const applyVitalUpdate = (
  current: VitalsDataPayload,
  update: Partial<VitalsDataPayload>
): Partial<VitalsDataPayload> => {
  if (
    update.bloodOxy &&
    shouldSkipBloodOxyUpdate(current.bloodOxy, update.bloodOxy)
  ) {
    const { bloodOxy: _bloodOxy, ...rest } = update;
    return rest;
  }

  return update;
};

export const mergeVitalMessages = (
  messages: VitalMessage[]
): Partial<VitalsDataPayload> => {
  const sorted = [...messages].sort(
    (a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0)
  );
  const merged: VitalsDataPayload = { status: 'pending' };

  sorted.forEach((message) => {
    if (message.typeString === VitalSensors.ECG) {
      return;
    }

    const update = generateVitalObject(message);
    const applied = applyVitalUpdate(merged, update);
    Object.assign(merged, applied);
  });

  const { status: _status, ...vitals } = merged;
  return vitals;
};

export const fetchVitalsHistory = (
  pubnub: PubNub,
  channels: string[]
): Promise<Partial<VitalsDataPayload>> =>
  new Promise((resolve) => {
    if (!channels.length) {
      resolve({});
      return;
    }

    pubnub.fetchMessages(
      {
        channels,
        count: 100,
      },
      (status, response) => {
        if (status.error) {
          resolve({});
          return;
        }

        const messages: VitalMessage[] = [];

        channels.forEach((channel) => {
          (response.channels[channel] ?? []).forEach((entry) => {
            const payload = entry.message as {
              type_string?: string;
              data?: unknown;
              timestamp?: number;
            };

            if (!payload?.type_string || payload.data === undefined) {
              return;
            }

            messages.push({
              typeString: payload.type_string,
              data: payload.data,
              timestamp:
                payload.timestamp ?? Math.floor(Number(entry.timetoken) / 10000),
            });
          });
        });

        resolve(mergeVitalMessages(messages));
      }
    );
  });
